import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import {
  ClipboardCheck, CheckCircle2, XCircle, AlertTriangle, RefreshCw,
  Loader2, ChevronDown, ChevronUp, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';

const PRIORITY_COLORS = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-gray-100 text-gray-600',
};

const STATUS_MAP = {
  pass: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', label: 'Pass' },
  fail: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50 border-red-200', label: 'Fail' },
  blocked: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', label: 'Blocked' },
};

export default function UatChecklist() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('UAT');
  const [scenarios, setScenarios] = useState([]);
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [noteInput, setNoteInput] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [scenRes, resRes, sumRes] = await Promise.all([
        api.get('/api/uat/scenarios'),
        api.get('/api/uat/results'),
        api.get('/api/uat/summary'),
      ]);
      setScenarios(scenRes.data.scenarios || []);
      setResults(resRes.data.results || []);
      setSummary(sumRes.data);
    } catch (e) { console.warn('[uat] fetch failed', e); } finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  const getResult = (scenarioId) => results.find(r => r.scenario_id === scenarioId);

  const submitResult = async (scenarioId, status) => {
    try {
      await api.post('/api/uat/results', { scenario_id: scenarioId, status, notes: noteInput });
      setNoteInput('');
      fetchData();
      toast.success(`Scenario marked as ${status}`);
    } catch { toast.error('Submit failed'); }
  };

  const seedTestUsers = async () => {
    try {
      const { data } = await api.post('/api/uat/seed-test-users');
      toast.success(data.message);
    } catch { toast.error('Seed failed'); }
  };

  if (loading) return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></main>
    </div>
  );

  const categories = [...new Set(scenarios.map(s => s.category))];

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="uat-checklist-page">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        <div className="space-y-6 animate-fade-in">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2">
                <ClipboardCheck className="w-6 h-6 text-[#D4A843]" /> User Acceptance Testing
              </h1>
              <p className="text-sm text-gray-500 mt-1">{scenarios.length} test scenarios for technicians and cannabis clients</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={seedTestUsers} className="rounded-lg text-xs" data-testid="uat-seed-users">
                <Users className="w-3.5 h-3.5 mr-1" /> Seed Test Users
              </Button>
              <Button size="sm" variant="outline" onClick={fetchData} className="rounded-lg text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
            </div>
          </div>

          {/* Summary */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="uat-summary">
              <div className="bg-white border border-gray-200 rounded-lg p-4 text-center"><p className="text-xl font-bold text-gray-900">{summary.total_scenarios}</p><p className="text-[10px] text-gray-500">Total</p></div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center"><p className="text-xl font-bold text-emerald-700">{summary.passed}</p><p className="text-[10px] text-emerald-600">Passed</p></div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center"><p className="text-xl font-bold text-red-700">{summary.failed}</p><p className="text-[10px] text-red-600">Failed</p></div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center"><p className="text-xl font-bold text-amber-700">{summary.blocked}</p><p className="text-[10px] text-amber-600">Blocked</p></div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center"><p className="text-xl font-bold text-gray-500">{summary.untested}</p><p className="text-[10px] text-gray-400">Untested</p></div>
              <div className="bg-[#D4A843]/5 border border-[#D4A843]/20 rounded-lg p-4 text-center"><p className="text-xl font-bold text-[#D4A843]">{summary.pass_rate}%</p><p className="text-[10px] text-[#D4A843]">Pass Rate</p></div>
            </div>
          )}

          {/* Scenarios by Category */}
          {categories.map(category => (
            <div key={category}>
              <h2 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">{category}</h2>
              <div className="space-y-2">
                {scenarios.filter(s => s.category === category).map(scenario => {
                  const result = getResult(scenario.id);
                  const isExpanded = expanded === scenario.id;
                  const statusStyle = result ? STATUS_MAP[result.status] : null;

                  return (
                    <div key={scenario.id} className={`bg-white border rounded-lg overflow-hidden ${result ? statusStyle.bg : 'border-gray-200'}`} data-testid={`uat-scenario-${scenario.id}`}>
                      <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setExpanded(isExpanded ? null : scenario.id)}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {result ? <statusStyle.icon className={`w-5 h-5 ${statusStyle.color} shrink-0`} /> : <div className="w-5 h-5 rounded-full border-2 border-gray-300 shrink-0" />}
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-bold text-gray-900">{scenario.title}</p>
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${PRIORITY_COLORS[scenario.priority]}`}>{scenario.priority}</span>
                            </div>
                            <p className="text-[10px] text-gray-500">{scenario.persona} | {scenario.steps.length} steps</p>
                          </div>
                        </div>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>

                      {isExpanded && (
                        <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3 animate-fade-in">
                          <div>
                            <p className="text-[10px] font-bold text-gray-900 uppercase tracking-wider mb-2">Steps:</p>
                            <ol className="space-y-1">
                              {scenario.steps.map((step, i) => (
                                <li key={`item-${i}`} className="text-xs text-gray-600 flex gap-2">
                                  <span className="text-[10px] font-bold text-gray-400 w-4 shrink-0">{i + 1}.</span> {step}
                                </li>
                              ))}
                            </ol>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-[10px] font-bold text-gray-900 uppercase tracking-wider">Expected Result:</p>
                            <p className="text-xs text-gray-600 mt-1">{scenario.expected}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <textarea value={noteInput} onChange={e => setNoteInput(e.target.value)} className="flex-1 text-xs border border-gray-200 rounded-lg p-2 h-16 resize-none focus:outline-none focus:ring-1 focus:ring-[#D4A843]" placeholder="Notes (optional)..." data-testid={`uat-notes-${scenario.id}`} />
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs" onClick={() => submitResult(scenario.id, 'pass')} data-testid={`uat-pass-${scenario.id}`}>
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Pass
                            </Button>
                            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs" onClick={() => submitResult(scenario.id, 'fail')} data-testid={`uat-fail-${scenario.id}`}>
                              <XCircle className="w-3 h-3 mr-1" /> Fail
                            </Button>
                            <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs" onClick={() => submitResult(scenario.id, 'blocked')} data-testid={`uat-blocked-${scenario.id}`}>
                              <AlertTriangle className="w-3 h-3 mr-1" /> Blocked
                            </Button>
                          </div>
                          {result?.notes && (
                            <div className="bg-gray-50 rounded-lg p-2"><p className="text-[10px] text-gray-500">Previous note: {result.notes}</p><p className="text-[10px] text-gray-400">By {result.tester_name} — {new Date(result.updated_at).toLocaleString()}</p></div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Test Credentials */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6" data-testid="uat-credentials">
            <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Test Accounts</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { role: 'Admin', email: process.env.REACT_APP_ADMIN_EMAIL || 'admin@techops.io', password: '••••••••••••' },
                { role: 'Technician', email: 'tech@techops.io', password: '••••••••••••' },
                { role: 'Customer', email: 'client@techops.io', password: '••••••••••••' },
              ].map(acc => (
                <div key={acc.role} className="bg-white rounded-lg p-3 border border-gray-200">
                  <p className="text-xs font-bold text-gray-900">{acc.role}</p>
                  <p className="text-[10px] text-gray-500 font-mono">{acc.email}</p>
                  <p className="text-[10px] text-gray-400 font-mono">{acc.password}</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-400 mt-3">Click "Seed Test Users" to create technician and customer accounts if they don't exist.</p>
          </div>
        </div>
      </main>
    </div>
  );
}
